import { message } from 'antd';
import {
  ApolloClient,
  HttpLink,
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
  ServerError,
  ServerParseError,
  concat,
  split,
} from '@apollo/client';
import { env } from '@utils/APIEndpoints';

import { onError } from '@apollo/client/link/error';
import { isBrowser } from '@utils/isBrowser';
import APIEndpoints from '@utils/APIEndpoints';
import { tryParse } from '@utils/formatter';
import { logout } from '@utils/auth';
import { destroyWebPushToken, unregisterServiceWorker } from '@utils/pushNotification';
import { destroyTour } from '@utils/webTour';

import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import Cookies from 'universal-cookie';

const authLink = new ApolloLink((operation, forward) => {
  const cookies = new Cookies();

  const authHeaders: Record<string, any> = {};
  const auth = isBrowser ? localStorage.getItem('auth') : null;
  if (auth) {
    const authObj = tryParse(auth);
    authHeaders.authorization = `Bearer ${authObj?.token}`;
  }

  const languageHeaders: Record<string, any> = {};
  const language = cookies.get('language');
  if (language) {
    languageHeaders.Language = language.language;
  }

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      ...authHeaders,
      ...languageHeaders,
      ...(operation.getContext().headers || {}),
    },
  }));

  return forward(operation);
});

// ensure that queries can run on the server during SSR and SSG
// @ts-ignore
global.fetch = require('node-fetch');
// @ts-ignore
let globalApolloClient: ApolloClient<NormalizedCacheObject> | null = null;

// ---------------
// MIDDLEWARE
// Middlewares are used to inspect and modify every request made over the link,
// for example, adding authentication tokens to every query.
// In order to add middleware, you simply create a new link and join it with the HttpLink
// --------------

const httpLink = ApolloLink.split(
  operation => operation.getContext().hasUpload,
  createUploadLink({ uri: APIEndpoints.GRAPHQL_URL }),
  new HttpLink({
    uri: APIEndpoints.GRAPHQL_URL,
    credentials: 'same-origin',
  }),
);

const errorHandling = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message: description, locations, path, extensions }) => {
      console.log(`[GraphQL error]: Message: ${description}, Location: ${JSON.stringify(locations)}, Path: ${path}`);
      switch (extensions?.code) {
        case 'UNAUTHENTICATED':
        case 'AUTHENTICATION_ERROR':
          if (window.location.href.indexOf('/login') < 0) {
            message.error(description);
          }
          logout();
          window.location.href = '/';

          // Handle web push notification when logout
          unregisterServiceWorker();
          destroyWebPushToken();

          // Handle web tour when logout
          destroyTour();
          break;
        case 'invalidOrderQuantity':
          document.dispatchEvent(new CustomEvent('invalidOrderQuantity', { detail: extensions }));
          break;
        case 'deleteReceivedWarrantyOrder':
          message.error('Không thể xoá đơn hàng đã được tiếp nhận');
          break;
        default:
          break;
      }
    });

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);

    // Read about list of error types here
    // https://www.apollographql.com/docs/link/links/http/#errors
    const error = networkError as ServerError | ServerParseError;

    if (error.statusCode) {
      switch (error.statusCode) {
        case 500: // Internal server error
          // No redirect but
          message.error('500 error!');
          break;
        case 404:
          message.error('Your request is invalid.');
          break;
        case 400: // Bad requests
          // mostly when user submit a form
          // TODO: global form error handling if needed
          break;
        case 401: // Unauthorized
          // TODO: forward user to login form, remember the target path
          // so you can redirect the user after login successfully
          break;
        case 403: // Forbidden
          break;
        default:
          message.error(
            'An error has occurred with the network. Try to refresh the page or close your browser and log in again.',
          );
      }
    } else {
      // normal Error
      message.error('There seems to be a problem with your network. Please check it and retry.');
    }
  }
});

// -------------------------------------
// Bundling middleware/afterware all together
let links = httpLink as ApolloLink;

links = concat(authLink, links);
links = concat(errorHandling, links);

export function createApolloClient(initialState = {}) {
  let splitLink: ApolloLink = links;
  if (isBrowser) {
    const authObject = tryParse(localStorage.getItem('auth') || '');
    const token = authObject?.token;

    if (token) {
      const wsUrl = `${APIEndpoints.WS_URL}?token=${token}`;
      // ActionCable request
      // eslint-disable-next-line
      const ActionCable = require('actioncable');
      const wsClient = ActionCable.createConsumer(wsUrl);
      const wsLink = new ActionCableLink({ cable: wsClient });

      splitLink = split(
        // split based on operation type
        ({ query }) => {
          const definition = getMainDefinition(query);
          return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        },
        wsLink as any,
        links,
      );
    }
  }

  return new ApolloClient({
    connectToDevTools: isBrowser && env !== 'production',
    ssrMode: !isBrowser,
    link: splitLink,
    cache: new InMemoryCache({
      typePolicies: {
        Variant: {
          // Disabling normalization
          keyFields: false,
        },
        // Short for options.mergeObjects(existing, incoming).
        Product: {
          fields: {
            defaultVariant: {
              merge: true,
            },
          },
        },
        OrderItem: {
          fields: {
            variant: {
              merge: true,
            },
          },
        },
        WarrantyOrderItem: {
          fields: {
            variant: {
              merge: true,
            },
          },
        },
        CampaignPropratedPrice: {
          keyFields: ['propratedPrice', 'id'],
        },
      },
    }).restore(initialState),
  });
}

export function initApolloClient(initialState = {}) {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  // ref https://github.com/zeit/next.js/blob/canary/examples/api-routes-apollo-server-and-client/apollo/client.js
  if (!isBrowser) {
    return createApolloClient(initialState);
  }

  if (!globalApolloClient) {
    globalApolloClient = createApolloClient(initialState);
  }

  return globalApolloClient;
}
